import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import type { SupportTicket } from '../../../api/support'
import { getCustomers } from '../../../api/customers';
import { useQuery } from '@tanstack/react-query';
import { addSupportTicket, updateSupportTicket } from '../../../api/support';

interface SupportTicketModalProps {
  open: boolean;
  ticket?: SupportTicket | null;
  onSuccess: (savedTicket: SupportTicket) => void;
  onCancel: () => void;
  destroyOnHidden?: boolean;
}

const SupportTicketModal: React.FC<SupportTicketModalProps> = ({ open, ticket, onSuccess, onCancel, destroyOnHidden }) => {
  const [form] = Form.useForm<SupportTicket>();
  const [loading, setLoading] = useState(false);

  // Fetch customers for dropdown only when adding a new ticket
  const { data: customers = [], isLoading: isLoadingCustomers } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const { customers } = await getCustomers();
      return customers;
    },
    enabled: open && !ticket, // Only fetch when adding
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (ticket) {
        form.setFieldsValue(ticket);
      }
    }
  }, [open, ticket, form]);

  const cleanData = (obj: any) => {
    const cleaned: any = {};
    Object.keys(obj).forEach(key => {
      if (obj[key] !== undefined) cleaned[key] = obj[key];
    });
    return cleaned;
  };

  const handleOk = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const selectedCustomer = customers.find(c => c.id === values.customer_id);
      
      let ticketData = {
        ...values,
        workspace_id: selectedCustomer?.workspace_id,
        workspace_name: selectedCustomer?.workspace_name,
        created_at: ticket?.created_at || new Date(),
        updated_at: new Date(),
      };
      ticketData = cleanData(ticketData);

      let savedTicket: SupportTicket;
      if (ticket?.id) {
        savedTicket = await updateSupportTicket(ticket.id, ticketData);
      } else {
        savedTicket = await addSupportTicket(ticketData);
      }
      
      message.success(ticket ? 'Support ticket updated successfully' : 'Support ticket created successfully');
      onSuccess(savedTicket);
      onCancel();
    } catch (error) {
      message.error('Failed to save support ticket');
      console.error('Error saving support ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={ticket ? 'Edit Support Ticket' : 'Add Support Ticket'}
      onOk={handleOk}
      onCancel={onCancel}
      destroyOnHidden={destroyOnHidden}
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical">
        <Form.Item 
          name="customer_id" 
          label="Customer" 
          rules={[{ required: true, message: 'Please select a customer' }]}
        >
          <Select
            placeholder="Select customer"
            loading={isLoadingCustomers}
            options={customers.map(customer => ({
              label: `${customer.primary_contact.name} (${customer.workspace_name || 'No Workspace'})`,
              value: customer.id,
            }))}
            disabled={!!ticket} // Disable when editing
          />
        </Form.Item>

        <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
          <Input placeholder="Enter subject" />
        </Form.Item>

        <Form.Item name="description" label="Description" rules={[{ required: true }]}>
          <Input.TextArea placeholder="Enter description" />
        </Form.Item>

        <Form.Item name="status" label="Status" rules={[{ required: true }]}>
          <Select 
            placeholder="Select status"
            options={[
              { value: 'Open', label: 'Open' }, 
              { value: 'In Progress', label: 'In Progress' }, 
              { value: 'Closed', label: 'Closed' }
            ]} 
          />
        </Form.Item>

        <Form.Item name="priority" label="Priority" rules={[{ required: true }]}>
          <Select 
            placeholder="Select priority"
            options={[
              { value: 'Low', label: 'Low' }, 
              { value: 'Medium', label: 'Medium' }, 
              { value: 'High', label: 'High' }
            ]} 
          />
        </Form.Item>

        <Form.Item name="assigned_to" label="Assigned To">
          <Input placeholder="Enter assignee" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SupportTicketModal; 